import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PublicReviewItem } from '../components/ClickableReviewsSummary';
import type { Review } from '../models/CreateReviewRequest';
import type { SpaceSummary } from '../models/Space';
import type { TutorSearchResult } from '../models/Tutor';
import { apiClient } from '../publicApiClient';
import { tutorApiClient } from '../tutorApiClient';
import { resolvePublicSpaceBySlug } from '../utils/publicSpaceResolution';

export type SpaceTutorsPageResolution =
  | { mode: 'slug'; city: string; spaceName: string }
  | { mode: 'id'; spaceId: string };

function reviewStatsFromReviews(reviews: Review[]): { averageRating: number; reviewCount: number } | null {
  if (reviews.length === 0) return null;
  const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
  return {
    averageRating: totalRating / reviews.length,
    reviewCount: reviews.length,
  };
}

async function loadSpaceReviews(spaceId: string): Promise<{
  reviews: Review[];
  reviewStats: { averageRating: number; reviewCount: number } | null;
}> {
  const reviews = await apiClient.getReviews(spaceId);
  return {
    reviews,
    reviewStats: reviewStatsFromReviews(reviews),
  };
}

async function loadTutorsForSpace(
  spaceId: string,
  onError: (message: string) => void,
): Promise<TutorSearchResult[]> {
  try {
    return await tutorApiClient.getPublicTutorsBySpaceId(spaceId);
  } catch (_e) {
    onError("Couldn't load tutors for this school.");
    return [];
  }
}

export function useSpaceTutorsPageData(resolution: SpaceTutorsPageResolution | null) {
  const { t } = useTranslation();
  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [reviewStats, setReviewStats] = useState<{ averageRating: number; reviewCount: number } | null>(null);
  const [spaceReviews, setSpaceReviews] = useState<Review[]>([]);
  const [spaceReviewsLoading, setSpaceReviewsLoading] = useState(false);
  const [tutorReviewsById, setTutorReviewsById] = useState<Record<string, PublicReviewItem[]>>({});
  const [tutorReviewsLoadingById, setTutorReviewsLoadingById] = useState<Record<string, boolean>>({});
  const [tutors, setTutors] = useState<TutorSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [spaceError, setSpaceError] = useState<string | null>(null);
  const [tutorError, setTutorError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!resolution) {
        setLoading(false);
        setSpaceError(t('schoolPage.loadError', "Couldn't load school details."));
        return;
      }

      setLoading(true);
      setSpaceError(null);
      setTutorError(null);

      try {
        if (resolution.mode === 'slug') {
          const { city, spaceName } = resolution;
          const availableCities = await apiClient.getCities().catch(() => []);
          const resolved = await resolvePublicSpaceBySlug(city, spaceName, availableCities);
          if (!resolved) {
            if (!cancelled) {
              setSpace(null);
              setReviewStats(null);
              setSpaceError(t('schoolPage.notFound', "We couldn't find this school."));
            }
            return;
          }

          if (cancelled) return;
          setSpace(resolved.summary);
          setReviewStats(resolved.searchResult.reviewStats ?? null);

          setSpaceReviewsLoading(true);
          try {
            const { reviews, reviewStats: statsFromReviews } = await loadSpaceReviews(resolved.summary.id);
            if (!cancelled) {
              setSpaceReviews(reviews);
              if (statsFromReviews) setReviewStats(statsFromReviews);
            }
          } catch (_e) {
            if (!cancelled) setSpaceReviews([]);
          } finally {
            if (!cancelled) setSpaceReviewsLoading(false);
          }

          const roster = await loadTutorsForSpace(resolved.summary.id, (message) => {
            if (!cancelled) {
              setTutorError(t('schoolPage.tutorsLoadError', message));
            }
          });
          if (!cancelled) setTutors(roster);
          return;
        }

        const summary = await apiClient.getSpaceSummary(resolution.spaceId);
        if (cancelled) return;
        setSpace(summary);

        setSpaceReviewsLoading(true);
        try {
          const { reviews, reviewStats: statsFromReviews } = await loadSpaceReviews(summary.id);
          if (!cancelled) {
            setSpaceReviews(reviews);
            setReviewStats(statsFromReviews);
          }
        } catch (_e) {
          if (!cancelled) setSpaceReviews([]);
        } finally {
          if (!cancelled) setSpaceReviewsLoading(false);
        }

        const roster = await loadTutorsForSpace(summary.id, (message) => {
          if (!cancelled) {
            setTutorError(t('schoolPage.tutorsLoadError', message));
          }
        });
        if (!cancelled) setTutors(roster);
      } catch (_e) {
        if (!cancelled) {
          setSpace(null);
          setReviewStats(null);
          setSpaceError(t('schoolPage.loadError', "Couldn't load school details."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // `t` is intentionally excluded: reloading page data on translation function identity changes
    // causes repeated network calls in tests and does not change the resolved space/tutor data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution?.mode, resolution?.mode === 'slug' ? resolution.city : null, resolution?.mode === 'slug' ? resolution.spaceName : null, resolution?.mode === 'id' ? resolution.spaceId : null]);

  const loadTutorReviews = async (tutorId: string) => {
    if (tutorReviewsById[tutorId] || tutorReviewsLoadingById[tutorId]) return;

    setTutorReviewsLoadingById((prev) => ({ ...prev, [tutorId]: true }));
    try {
      const reviews = await tutorApiClient.getTutorReviews(tutorId);
      setTutorReviewsById((prev) => ({
        ...prev,
        [tutorId]: reviews.map((review) => ({
          id: review.id,
          rating: review.rating,
          comment: review.comment,
          createdAt: review.createdAt,
          username: review.username,
        })),
      }));
    } catch (_e) {
      setTutorReviewsById((prev) => ({ ...prev, [tutorId]: [] }));
    } finally {
      setTutorReviewsLoadingById((prev) => ({ ...prev, [tutorId]: false }));
    }
  };

  return {
    space,
    reviewStats,
    spaceReviews,
    spaceReviewsLoading,
    tutors,
    loading,
    spaceError,
    tutorError,
    tutorReviewsById,
    tutorReviewsLoadingById,
    loadTutorReviews,
  };
}
